import React, { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';

export const toolId = value => value?.id || value?._id || '';
export const toolPath = documentNumber => `/quality/test-tools/${encodeURIComponent(documentNumber)}`;
export const artefactPath = (releaseId, name) => `/quality/software-releases/${encodeURIComponent(releaseId)}/artefacts/${encodeURIComponent(name)}/download`;
export const currentArtefactPath = (documentNumber, name) => `${toolPath(documentNumber)}/current/artefacts/${encodeURIComponent(name)}/download`;
export const toolTypes = ['calibration', 'test', 'programming', 'imaging', 'labelling', 'utility', 'runtime'];
export const supplierKeys = ['gpv', 'ubiqconn', 'acmepoint', 'hk_ringer'];
export const fieldStyle = { width: '100%', boxSizing: 'border-box', padding: '.6rem', borderRadius: 6,
  border: '1px solid var(--ni-border-soft)', background: 'var(--ni-panel)', color: 'var(--ni-text)' };
export const panelStyle = { padding: '1rem', border: '1px solid var(--ni-border-soft)', borderRadius: 10, marginBottom: '1rem' };
export const mutedStyle = { color: 'var(--ni-text-muted)', fontSize: '.9rem' };

function downloadFilename(disposition) {
  if (!disposition) return '';
  let value = '';
  try {
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    value = encoded ? decodeURIComponent(encoded[1].trim()) : (disposition.match(/filename="([^"]+)"/i)?.[1] || '');
  } catch { return ''; }
  const unsafe = Array.from(value).some(character => ['/', '\\'].includes(character) || character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);
  return value && value.length <= 255 && !unsafe && !['.', '..'].includes(value) ? value : '';
}

export function useVisibleRefresh(onRefresh, enabled = true) {
  const callback = useRef(onRefresh);
  callback.current = onRefresh;
  useEffect(() => {
    if (!enabled) return undefined;
    let lastEvent = -Infinity;
    const refresh = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastEvent < 1000) return;
      lastEvent = Date.now(); callback.current();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [enabled]);
}

export function useToolApi() {
  const { getToken } = useAuth();
  return useCallback(async (path, options = {}, format = 'json') => {
    const token = await getToken();
    if (options.signal?.aborted) throw new DOMException('Request cancelled', 'AbortError');
    if (!token) throw Object.assign(new Error('Sign in to access tools and software.'), { status: 401 });
    const response = await fetch(`${(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '')}${path}`, {
      ...options, cache: 'no-store', credentials: 'omit', headers: { Authorization: `Bearer ${token}`,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}) },
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });
    if (format === 'download' && response.ok) return {
      blob: await response.blob(), filename: downloadFilename(response.headers?.get?.('Content-Disposition')),
      version: response.headers?.get?.('X-Tool-Version') || '', releaseId: response.headers?.get?.('X-Tool-Release-Id') || '',
      sha256: response.headers?.get?.('X-Artifact-SHA256') || '',
    };
    if (format === 'blob' && response.ok) return response.blob();
    let body;
    try { body = await response.json(); } catch { throw Object.assign(new Error(`Unreadable server response (${response.status}).`), { status: response.status }); }
    if (!response.ok || body.ok === false) throw Object.assign(new Error(body.error || body.message || `Request failed (${response.status}).`), { status: response.status, data: body });
    return body;
  }, [getToken]);
}

export function ServerError({ error }) {
  if (!error) return null;
  const blockers = error.data?.blockers || [];
  return <div role="alert" style={{ ...panelStyle, color: 'var(--ni-text)' }}>
    <p>{error.message}</p>
    {!!blockers.length && <ul>{blockers.map((item, index) => <li key={index}>{typeof item === 'string' ? item : item.message || item.code}</li>)}</ul>}
  </div>;
}

export function DeliveryNotice({ deliveryStatus, currentRelease }) {
  if (!deliveryStatus || !['uploading', 'publishing', 'failed'].includes(deliveryStatus.status)) return null;
  const available = currentRelease ? `Current available version is ${currentRelease.tag}.` : 'No complete package is available yet.';
  return deliveryStatus.status === 'failed'
    ? <p role="alert">Delivery failed for version {deliveryStatus.tag}. {available}</p>
    : <p role="status">New version {deliveryStatus.tag} is being delivered. {available}</p>;
}
